import { auth } from './auth-store.js';

interface Route {
  pattern: string;
  component: string;
  guard?: 'auth' | 'admin';
}

interface MatchResult {
  route: Route;
  params: Record<string, string>;
  query: Record<string, string>;
}

/** Return false to block navigation */
type NavigationGuard = () => boolean;

class Router {
  private routes: Route[] = [];
  private outlet: HTMLElement | null = null;
  private guard: NavigationGuard | null = null;
  private previousHash = '';

  register(routes: Route[]): void {
    this.routes = routes;
  }

  setOutlet(el: HTMLElement): void {
    this.outlet = el;
  }

  /** Set a guard that can block navigation. Only one guard at a time. */
  setGuard(guard: NavigationGuard): () => void {
    this.guard = guard;
    return () => { if (this.guard === guard) this.guard = null; };
  }

  /** Check if navigation is allowed (calls current guard). */
  canLeave(): boolean {
    return !this.guard || this.guard();
  }

  start(): void {
    this.previousHash = location.hash;
    window.addEventListener('hashchange', () => this.onHashChange());
    this.resolve();
  }

  navigate(hash: string): void {
    location.hash = hash;
  }

  private onHashChange(): void {
    // Check guard before allowing navigation
    if (this.guard && !this.guard()) {
      // Block — restore previous hash without triggering another hashchange
      history.replaceState(null, '', this.previousHash);
      return;
    }
    this.previousHash = location.hash;
    this.resolve();
  }

  private resolve(): void {
    const hash = location.hash.slice(1) || '/schedule';
    const match = this.match(hash);

    if (!match) {
      this.navigate('/schedule');
      return;
    }

    // Guard checks
    if (match.route.guard === 'auth' || match.route.guard === 'admin') {
      if (!auth.isLoggedIn) {
        this.navigate('/login');
        return;
      }
    }
    if (match.route.guard === 'admin' && !auth.isAdmin) {
      this.navigate('/availability');
      return;
    }

    // Redirect logged-in users away from login
    if (match.route.pattern === '/login' && auth.isLoggedIn) {
      this.navigate(auth.isAdmin ? '/admin/dashboard' : '/availability');
      return;
    }

    this.render(match);
  }

  private match(hash: string): MatchResult | null {
    // Split off query string
    const [path, queryStr] = hash.split('?');
    const query: Record<string, string> = {};
    if (queryStr) {
      for (const pair of queryStr.split('&')) {
        const [k, v] = pair.split('=');
        query[decodeURIComponent(k)] = decodeURIComponent(v ?? '');
      }
    }

    const pathParts = path.split('/').filter(Boolean);

    for (const route of this.routes) {
      const routeParts = route.pattern.split('/').filter(Boolean);
      if (routeParts.length !== pathParts.length) continue;

      const params: Record<string, string> = {};
      let matched = true;

      for (let i = 0; i < routeParts.length; i++) {
        if (routeParts[i].startsWith(':')) {
          params[routeParts[i].slice(1)] = pathParts[i];
        } else if (routeParts[i] !== pathParts[i]) {
          matched = false;
          break;
        }
      }

      if (matched) return { route, params, query };
    }

    return null;
  }

  private render(match: MatchResult): void {
    if (!this.outlet) return;

    const el = document.createElement(match.route.component);

    // Set params as attributes
    for (const [key, val] of Object.entries(match.params)) {
      el.setAttribute(key, val);
    }
    for (const [key, val] of Object.entries(match.query)) {
      el.setAttribute(key, val);
    }

    this.outlet.innerHTML = '';
    this.outlet.appendChild(el);
  }
}

export const router = new Router();
